import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, MessageSquare } from 'lucide-react';
import FeedbackFormModal from '../components/FeedbackFormModal';
import api from '../lib/api';
import { useFeatureFlags } from '@shared/flags/FeatureFlagProvider';
import { handleLoginSuccess } from '../utils/handleLoginSuccess';
import { getSessionEndReason, clearSessionEndReason } from '@shared/auth/session';

// Mirrors ACCOUNT_DEACTIVATED_MESSAGE in backend/src/constants/authMessages.js.
// The login response carries the text, but a mid-session deactivation arrives
// here as a stored reason with no response body, so the copy lives in both.
const ACCOUNT_DEACTIVATED_MESSAGE =
  'Your account has been deactivated. Please contact support.';

const LoginPage: React.FC = () => {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Set when the last login was blocked by the concurrent-session cap.
  const [capBlocked, setCapBlocked] = useState(false);
  const [signingOutOthers, setSigningOutOthers] = useState(false);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { identifyUser, flags } = useFeatureFlags();
  const returnUrl = searchParams.get('returnUrl') || '/dashboard';
  const isDisabled = !identifier || !password || loading;

  // Explain why the user landed back here (e.g. a 60-min inactivity logout).
  useEffect(() => {
    const reason = getSessionEndReason();
    if (reason === 'expired' || reason === 'session_ended') {
      setNotice('Your session expired. Please sign in again.');
    } else if (reason === 'deactivated') {
      // Signing in again will not help, so this is an error, not a notice.
      setError(ACCOUNT_DEACTIVATED_MESSAGE);
    }
    clearSessionEndReason();
  }, []);

  const doLogin = async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await api.post('/auth/login', { identifier, password });

      if (res.data?.success && res.data?.data?.token) {
        setCapBlocked(false);
        handleLoginSuccess({
          data: res.data.data,
          navigate,
          identifyUser,
          returnUrl,
          citizenEnabled: flags.citizen_enabled,
        });
      } else {
        // Surface a server-provided message (e.g. "Your account has been
        // deactivated…") even when the response resolved without a token. The
        // fallback covers a 2xx whose body isn't ours at all — an edge/CDN
        // error page, say — so it stays in the user's language, not the
        // transport's.
        setError(
          res.data?.error ||
            "We couldn't sign you in. Please try again, or contact support if this continues."
        );
      }
    } catch (err: any) {
      const code = err.response?.data?.code;
      if (code === 'SESSION_LIMIT') {
        // Too many active sessions for this credential — offer recovery.
        setCapBlocked(true);
        setError(err.response?.data?.error || "You're already signed in on another device.");
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNotice(null);
    await doLogin();
  };

  // Cap-recovery: prove ownership with the same credentials, revoke the other
  // sessions, then retry the login automatically.
  const handleSignOutOthers = async () => {
    setSigningOutOthers(true);
    setError(null);
    try {
      await api.post('/auth/logout-others', { identifier, password });
      setCapBlocked(false);
      await doLogin();
    } catch {
      setError('Could not sign out your other devices. Please re-enter your password and try again.');
    } finally {
      setSigningOutOthers(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background dark:bg-background-dark-primary">
      <div className="max-w-md w-full mx-auto card">
        <h1 className="text-3xl font-bold text-center mb-2 font-gilroy text-primary-700 dark:text-primary-dark-300">Capitol Commons</h1>
        <p className="text-center text-text-secondary dark:text-text-dark-secondary mb-6">
          Sign in to access legislative tracking and collaboration tools
        </p>
        {notice && (
          <div
            className="mb-4 rounded-lg bg-primary-50 dark:bg-primary-900/30 text-primary-800 dark:text-primary-200 text-sm text-center px-3 py-2"
            role="status"
            aria-live="polite"
          >
            {notice}
          </div>
        )}
        <form className="space-y-4" onSubmit={handleSubmit} autoComplete="on">
          <div>
            <label htmlFor="identifier" className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
              Email or Username <span className="text-error-600" aria-label="required">*</span>
            </label>
            <input
              type="text"
              id="identifier"
              className="input"
              placeholder="Enter your email or username"
              value={identifier}
              onInput={e => setIdentifier((e.target as HTMLInputElement).value)}
              autoComplete="username"
              required
              aria-required="true"
              aria-invalid={error ? 'true' : 'false'}
              aria-describedby={error ? 'login-error' : undefined}
            />
          </div>
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
              Password <span className="text-error-600" aria-label="required">*</span>
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                className="input pr-10"
                placeholder="Enter your password"
                value={password}
                onInput={e => setPassword((e.target as HTMLInputElement).value)}
                autoComplete="current-password"
                required
                aria-required="true"
                aria-invalid={error ? 'true' : 'false'}
                aria-describedby={error ? 'login-error' : undefined}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary dark:text-text-dark-tertiary hover:text-text-primary dark:hover:text-text-dark-primary transition-colors"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
          {error && (
            <div
              id="login-error"
              className="text-error-600 text-sm font-medium text-center"
              role="alert"
              aria-live="assertive"
            >
              {error}
            </div>
          )}
          {capBlocked && (
            <div className="rounded-lg bg-warning-50 dark:bg-warning-900/30 px-3 py-3 text-sm text-text-secondary dark:text-text-dark-secondary space-y-2">
              <p>
                This account is already signed in on the maximum number of devices.
                You can sign out the other devices and continue here.
              </p>
              <button
                type="button"
                onClick={handleSignOutOthers}
                disabled={signingOutOthers || !identifier || !password}
                className="px-4 font-medium transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed w-full bg-warning-600 text-white font-semibold py-2 rounded-2xl disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {signingOutOthers ? 'Signing out other devices…' : 'Sign out other devices & continue'}
              </button>
            </div>
          )}
          <button
            type="submit"
            className="px-4 font-medium transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed w-full bg-primary-700 text-white font-semibold text-lg py-2 rounded-2xl disabled:opacity-60 disabled:cursor-not-allowed"
            disabled={isDisabled}
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>
        <div className="mt-3 text-center text-sm space-y-2">
          <div>
            <a
              href="/forgot-password"
              className="text-primary-700 dark:text-primary-400 hover:underline"
            >
              Forgot your password?
            </a>
          </div>
          <div>
            <button
              type="button"
              onClick={() => setIsSupportModalOpen(true)}
              className="inline-flex items-center gap-1 text-primary-700 dark:text-primary-400 hover:underline"
            >
              <MessageSquare className="w-4 h-4" />
              Contact Support
            </button>
          </div>
        </div>
        <p className="mt-4 text-center text-sm text-text-tertiary dark:text-text-dark-tertiary">
          By logging in, you agree to our{' '}
          <a 
            href="https://capitolcommons.ai/terms" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="text-primary-600 dark:text-primary-400 hover:underline"
          >
            Terms of Use
          </a>
          {' '}and{' '}
          <a 
            href="https://capitolcommons.ai/privacy" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="text-primary-600 dark:text-primary-400 hover:underline"
          >
            Privacy Policy
          </a>.
        </p>
      </div>

      <FeedbackFormModal
        isOpen={isSupportModalOpen}
        onClose={() => setIsSupportModalOpen(false)}
        isPublic={true}
      />
    </div>
  );
};

export default LoginPage;
